import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoiceApi } from '../../utils/purchasingApi';
import { usePermission } from '../../hooks/usePermission';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Purchasing.css';

const STATUS_LABELS = {
  DRAFT:     { label: 'Draft',     cls: 'purch-badge--draft' },
  SUBMITTED: { label: 'Submitted', cls: 'purch-badge--submitted' },
  APPROVED:  { label: 'Approved',  cls: 'purch-badge--approved' },
  POSTED:    { label: 'Posted',    cls: 'purch-badge--posted' },
  CANCELLED: { label: 'Cancelled', cls: 'purch-badge--cancelled' },
};

const fmt = (n) => Number(n || 0).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—';

export default function PurchaseInvoicesPage() {
  const navigate = useNavigate();
  const canCreate  = usePermission('purchases.invoice.create');
  const canApprove = usePermission('purchases.invoice.approve');
  const canPost    = usePermission('purchases.invoice.post');

  const [invoices, setInvoices] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ status: '', search: '' });
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError]     = useState('');
  const [rejectReason, setRejectReason]   = useState('');
  const [cancelReason, setCancelReason]   = useState('');
  const [showReject, setShowReject]   = useState(false);
  const [showCancel, setShowCancel]   = useState(false);
  const [uploadFile, setUploadFile]   = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const LIMIT = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoiceApi.list({ ...filters, page, limit: LIMIT });
      setInvoices(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const refreshSelected = async (id) => {
    try {
      const inv = await invoiceApi.getById(id);
      setSelected(inv);
      load();
    } catch (e) { console.error(e); }
  };

  const doAction = async (fn, label) => {
    setActionLoading(true);
    setActionError('');
    try {
      await fn();
      await refreshSelected(selected.id);
    } catch (e) {
      setActionError(e.message || `${label} failed`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploadLoading(true);
    setActionError('');
    try {
      await invoiceApi.uploadAttachment(selected.id, uploadFile);
      setUploadFile(null);
      await refreshSelected(selected.id);
    } catch (e) {
      setActionError(e.message || 'Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="purch-page">
      <div className="purch-header">
        <div>
          <h1 className="purch-title">Purchase Invoices</h1>
          <p className="purch-subtitle">{total} invoice{total !== 1 ? 's' : ''}</p>
        </div>
        {canCreate && (
          <button className="purch-btn purch-btn--primary" onClick={() => navigate('/purchasing/invoices/new')}>
            <i className="fas fa-plus" /> New Invoice
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="purch-filters">
        <select
          className="purch-select"
          value={filters.status}
          onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <input
          className="purch-search"
          placeholder="Search vendor, ref…"
          value={filters.search}
          onChange={(e) => { setFilters(f => ({ ...f, search: e.target.value })); setPage(1); }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner variant="default" />
      ) : invoices.length === 0 ? (
        <div className="purch-empty">
          <i className="fas fa-file-invoice fa-3x" />
          <p>No purchase invoices found</p>
          {canCreate && (
            <button className="purch-btn purch-btn--primary" onClick={() => navigate('/purchasing/invoices/new')}>
              Create First Invoice
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="purch-table-wrap">
            <table className="purch-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>PO #</th>
                  <th>Vendor</th>
                  <th>Date</th>
                  <th>Vendor Ref</th>
                  <th>Lines</th>
                  <th className="purch-table__amount">Total (EGP)</th>
                  <th>Status</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const sb = STATUS_LABELS[inv.status] || {};
                  return (
                    <tr
                      key={inv.id}
                      className="purch-table__row--clickable"
                      onClick={() => { setSelected(inv); setActionError(''); setShowReject(false); setShowCancel(false); }}
                    >
                      <td><strong>INV-{inv.fiscalYear?.code}-{String(inv.invoiceNumber).padStart(4, '0')}</strong></td>
                      <td>PO-{inv.po?.poNumber}</td>
                      <td>{inv.vendor?.name}</td>
                      <td>{fmtDate(inv.date)}</td>
                      <td>{inv.vendorTaxInvoiceNumber || '—'}</td>
                      <td>{inv._count?.lines ?? 0}</td>
                      <td className="purch-table__amount">{fmt(inv.totalAmount)}</td>
                      <td><span className={`purch-badge ${sb.cls}`}>{sb.label}</span></td>
                      <td>{inv.createdBy?.fullName}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="purch-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="purch-btn purch-btn--sm">‹ Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="purch-btn purch-btn--sm">Next ›</button>
            </div>
          )}
        </>
      )}

      {/* Detail slide-over */}
      {selected && (
        <div className="purch-overlay" onClick={() => setSelected(null)}>
          <div className="purch-slideover" onClick={(e) => e.stopPropagation()}>
            <div className="purch-slideover__header">
              <div>
                <h2 className="purch-slideover__title">
                  INV-{selected.fiscalYear?.code}-{String(selected.invoiceNumber).padStart(4, '0')}
                </h2>
                <span className={`purch-badge ${STATUS_LABELS[selected.status]?.cls}`}>
                  {STATUS_LABELS[selected.status]?.label}
                </span>
              </div>
              <button className="purch-slideover__close" onClick={() => setSelected(null)}>
                <i className="fas fa-times" />
              </button>
            </div>

            {actionError && (
              <div className="purch-alert purch-alert--error">{actionError}</div>
            )}

            <div className="purch-slideover__body">
              {/* Meta grid */}
              <div className="purch-detail-grid">
                <div><span className="purch-detail-label">PO</span><span>PO-{selected.po?.poNumber}</span></div>
                <div><span className="purch-detail-label">Vendor</span><span>{selected.vendor?.name}</span></div>
                <div><span className="purch-detail-label">Date</span><span>{fmtDate(selected.date)}</span></div>
                <div><span className="purch-detail-label">Vendor Ref #</span><span>{selected.vendorTaxInvoiceNumber || '—'}</span></div>
                <div><span className="purch-detail-label">Entity</span><span>{selected.entity?.name || '—'}</span></div>
                <div><span className="purch-detail-label">Currency</span><span>{selected.currency?.code}</span></div>
                {selected.notes && (
                  <div className="purch-detail-grid__full"><span className="purch-detail-label">Notes</span><span>{selected.notes}</span></div>
                )}
              </div>

              {/* Rejection notice */}
              {selected.rejectionReason && (
                <div className="purch-alert purch-alert--warn">
                  <strong>Rejection reason:</strong> {selected.rejectionReason}
                </div>
              )}

              {/* Lines */}
              <h3 className="purch-section-title">Lines</h3>
              <div className="purch-table-wrap">
                <table className="purch-table purch-table--sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit Price</th>
                      <th>Tax %</th>
                      <th>Tax Amt</th>
                      <th>Line Total</th>
                      <th>Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.lines || []).map((l) => (
                      <tr key={l.id}>
                        <td>{l.lineNumber}</td>
                        <td>{l.description}</td>
                        <td>{fmt(l.quantity)}</td>
                        <td className="purch-table__amount">{fmt(l.unitPrice)}</td>
                        <td>{Number(l.taxRate)}%</td>
                        <td className="purch-table__amount">{fmt(l.taxAmount)}</td>
                        <td className="purch-table__amount"><strong>{fmt(l.lineTotal)}</strong></td>
                        <td className="purch-table__account">
                          {l.account ? `${l.account.code} — ${l.account.name}` : <span className="purch-warn">No account</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} />
                      <td className="purch-table__amount"><strong>Tax:</strong></td>
                      <td className="purch-table__amount">{fmt(selected.taxAmount)}</td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={5} />
                      <td className="purch-table__amount"><strong>Total:</strong></td>
                      <td className="purch-table__amount"><strong>{fmt(selected.totalAmount)}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Voucher link */}
              {selected.postedVoucher && (
                <div className="purch-voucher-link">
                  <i className="fas fa-file-invoice-dollar" />
                  {' '}Posted as{' '}
                  <button
                    className="purch-link-btn"
                    onClick={() => navigate(`/vouchers/${selected.postedVoucherId}`)}
                  >
                    PV-{selected.postedVoucher.voucherNumber}
                  </button>
                </div>
              )}

              {/* Attachment */}
              <div className="purch-attachment-section">
                <h3 className="purch-section-title">Vendor Tax Invoice</h3>
                {selected.vendorInvoiceFile ? (
                  <div className="purch-attachment-row">
                    <i className="fas fa-paperclip" />
                    <a href={selected.vendorInvoiceFile} target="_blank" rel="noreferrer">
                      View attached file
                    </a>
                  </div>
                ) : (
                  <p className="purch-detail-label">No file attached</p>
                )}
                {selected.status !== 'POSTED' && selected.status !== 'CANCELLED' && canCreate && (
                  <div className="purch-upload-row">
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      id="inv-attach"
                      style={{ display: 'none' }}
                      onChange={(e) => setUploadFile(e.target.files[0] || null)}
                    />
                    <label htmlFor="inv-attach" className="purch-btn purch-btn--sm">
                      <i className="fas fa-upload" /> Choose File
                    </label>
                    {uploadFile && <span className="purch-filename">{uploadFile.name}</span>}
                    {uploadFile && (
                      <button
                        className="purch-btn purch-btn--primary purch-btn--sm"
                        onClick={handleUpload}
                        disabled={uploadLoading}
                      >
                        {uploadLoading ? <LoadingSpinner variant="button" /> : 'Upload'}
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Audit trail */}
              <div className="purch-trail">
                {selected.submittedBy && <p><strong>Submitted by:</strong> {selected.submittedBy.fullName} on {fmtDate(selected.submittedAt)}</p>}
                {selected.approvedBy  && <p><strong>Approved by:</strong>  {selected.approvedBy.fullName}  on {fmtDate(selected.approvedAt)}</p>}
                {selected.rejectedBy  && <p><strong>Rejected by:</strong>  {selected.rejectedBy.fullName}  on {fmtDate(selected.rejectedAt)}</p>}
                {selected.postedBy    && <p><strong>Posted by:</strong>    {selected.postedBy.fullName}    on {fmtDate(selected.postedAt)}</p>}
                {selected.cancelledBy && <p><strong>Cancelled by:</strong> {selected.cancelledBy.fullName} on {fmtDate(selected.cancelledAt)} — {selected.cancellationReason}</p>}
              </div>
            </div>

            {/* Actions */}
            <div className="purch-slideover__actions">
              {selected.status === 'DRAFT' && canCreate && (
                <>
                  <button
                    className="purch-btn purch-btn--primary"
                    disabled={actionLoading}
                    onClick={() => doAction(() => invoiceApi.submit(selected.id), 'Submit')}
                  >
                    {actionLoading ? <LoadingSpinner variant="button" /> : 'Submit for Approval'}
                  </button>
                  <button
                    className="purch-btn purch-btn--outline"
                    onClick={() => navigate(`/purchasing/invoices/${selected.id}/edit`)}
                  >
                    <i className="fas fa-edit" /> Edit
                  </button>
                  <button
                    className="purch-btn purch-btn--danger"
                    disabled={actionLoading}
                    onClick={() => doAction(async () => { await invoiceApi.delete(selected.id); setSelected(null); load(); }, 'Delete')}
                  >
                    Delete
                  </button>
                </>
              )}

              {selected.status === 'SUBMITTED' && canApprove && !showReject && (
                <>
                  <button
                    className="purch-btn purch-btn--primary"
                    disabled={actionLoading}
                    onClick={() => doAction(() => invoiceApi.approve(selected.id), 'Approve')}
                  >
                    {actionLoading ? <LoadingSpinner variant="button" /> : 'Approve'}
                  </button>
                  <button className="purch-btn purch-btn--danger purch-btn--outline" onClick={() => setShowReject(true)}>
                    Reject
                  </button>
                </>
              )}

              {selected.status === 'APPROVED' && canPost && !showReject && (
                <button
                  className="purch-btn purch-btn--success"
                  disabled={actionLoading}
                  onClick={() => doAction(() => invoiceApi.post(selected.id), 'Post')}
                >
                  {actionLoading ? <LoadingSpinner variant="button" /> : <><i className="fas fa-check-circle" /> Post Invoice (Generate PV)</>}
                </button>
              )}
              {selected.status === 'APPROVED' && canApprove && !showReject && (
                <button className="purch-btn purch-btn--danger purch-btn--outline" onClick={() => setShowReject(true)}>
                  Reject
                </button>
              )}

              {/* Reject sub-form */}
              {showReject && (
                <div className="purch-reason-form">
                  <textarea
                    className="purch-textarea"
                    placeholder="Rejection reason (required)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={2}
                  />
                  <div className="purch-reason-btns">
                    <button
                      className="purch-btn purch-btn--danger"
                      disabled={!rejectReason.trim() || actionLoading}
                      onClick={() => doAction(async () => { await invoiceApi.reject(selected.id, rejectReason); setRejectReason(''); setShowReject(false); }, 'Reject')}
                    >
                      Confirm Reject
                    </button>
                    <button className="purch-btn purch-btn--outline" onClick={() => setShowReject(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Cancel (any non-terminal, non-posted status) */}
              {['DRAFT', 'SUBMITTED', 'APPROVED'].includes(selected.status) && canCreate && !showCancel && !showReject && (
                <button className="purch-btn purch-btn--ghost" onClick={() => setShowCancel(true)}>
                  Cancel Invoice
                </button>
              )}
              {showCancel && (
                <div className="purch-reason-form">
                  <textarea
                    className="purch-textarea"
                    placeholder="Cancellation reason (required)"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={2}
                  />
                  <div className="purch-reason-btns">
                    <button
                      className="purch-btn purch-btn--danger"
                      disabled={!cancelReason.trim() || actionLoading}
                      onClick={() => doAction(async () => { await invoiceApi.cancel(selected.id, cancelReason); setCancelReason(''); setShowCancel(false); }, 'Cancel')}
                    >
                      Confirm Cancel
                    </button>
                    <button className="purch-btn purch-btn--outline" onClick={() => setShowCancel(false)}>Back</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
